'use client';

import { useEffect, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { marketplaceApi, type ReportReason, type ReportTargetType } from '@/lib/api';
import { haptics } from '@/lib/native';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

const REASONS: { value: ReportReason; label: string; hint: string }[] = [
  { value: 'SPAM', label: 'Spam or repetitive', hint: 'Unwanted, repeated, or off-topic posting' },
  { value: 'SCAM_OR_FRAUD', label: 'Scam or fraud', hint: 'Fake listing, payment trickery, phishing' },
  { value: 'HARASSMENT', label: 'Harassment or hate', hint: 'Threats, abuse, or hateful speech' },
  { value: 'INAPPROPRIATE', label: 'Inappropriate content', hint: 'Sexual, violent, or otherwise unsuitable' },
  { value: 'COUNTERFEIT', label: 'Counterfeit or unsafe', hint: 'Fake goods, or food that looks unsafe' },
  { value: 'OTHER', label: 'Something else', hint: 'Tell us what is wrong below' },
];

const TARGET_NOUN: Record<ReportTargetType, string> = {
  BUSINESS: 'store',
  LISTING: 'listing',
  MESSAGE: 'message',
  CONVERSATION: 'conversation',
  RATING: 'review',
  USER: 'person',
};

export interface ReportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  targetType: ReportTargetType;
  targetId: string;
  /** Name shown in the sheet header, e.g. the store or listing title. */
  targetLabel?: string;
}

/**
 * Report sheet for user-generated content (App Store guideline 1.2).
 *
 * Built on the Radix Dialog primitives rather than the marketplace
 * `BottomSheet`: that one is a drag-detent panel for the map, with no backdrop
 * and no focus trap. Here the same content slides up from the bottom edge on
 * phones and becomes a centered modal from `sm:` up.
 */
export function ReportSheet({
  open,
  onOpenChange,
  targetType,
  targetId,
  targetLabel,
}: ReportSheetProps) {
  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setReason(null);
      setDetails('');
      setSubmitting(false);
    }
  }, [open]);

  const noun = TARGET_NOUN[targetType];

  const submit = async () => {
    if (!reason || submitting) return;
    setSubmitting(true);
    try {
      const result = await marketplaceApi.report({
        targetType,
        targetId,
        reason,
        details: details.trim() || undefined,
      });
      void haptics.success();
      toast.success(
        result.duplicate
          ? 'You already reported this — we are still reviewing it.'
          : "Thanks, we'll review this",
      );
      onOpenChange(false);
    } catch (err: any) {
      void haptics.error();
      const message: string = err?.message || 'Could not send the report';
      toast.error(
        /unauthor|401|token/i.test(message)
          ? 'Sign in to report this content'
          : message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[70] bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-x-0 bottom-0 z-[71] flex max-h-[88vh] flex-col gap-4 rounded-t-2xl border-t bg-background p-5 shadow-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom duration-200',
            'sm:inset-x-auto sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:w-full sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl sm:border',
            'sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=open]:slide-in-from-bottom-0',
          )}
          style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
        >
          {/* Drag affordance — the sheet is tap-to-dismiss, but the grabber is
              what tells a phone user this panel came up from the bottom. */}
          <div className="mx-auto h-1 w-10 shrink-0 rounded-full bg-muted-foreground/30 sm:hidden" />

          <div className="space-y-1">
            <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
              Report this {noun}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-muted-foreground">
              {targetLabel && <span className="font-medium text-foreground">{targetLabel}. </span>}
              We review every report and act on content that breaks our rules.
            </DialogPrimitive.Description>
          </div>

          <div className="-mx-1 flex-1 space-y-1 overflow-y-auto px-1" role="radiogroup">
            {REASONS.map((option) => {
              const selected = reason === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => {
                    void haptics.selection();
                    setReason(option.value);
                  }}
                  className={cn(
                    'flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                    selected
                      ? 'border-primary bg-primary/5'
                      : 'border-transparent hover:bg-accent',
                  )}
                >
                  <span
                    className={cn(
                      'mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border',
                      selected ? 'border-primary' : 'border-muted-foreground/40',
                    )}
                  >
                    {selected && <span className="h-2 w-2 rounded-full bg-primary" />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{option.label}</span>
                    <span className="block text-xs text-muted-foreground">{option.hint}</span>
                  </span>
                </button>
              );
            })}

            <Textarea
              value={details}
              onChange={(e) => setDetails(e.target.value.slice(0, 1000))}
              placeholder="Add anything that helps us understand (optional)"
              className="mt-2 min-h-[72px]"
              maxLength={1000}
            />
          </div>

          <div className="flex shrink-0 gap-2">
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="flex-1"
              onClick={submit}
              disabled={!reason || submitting}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit report
            </Button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
